import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import { styled, useTheme } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { invalidateUserSessionCredits } from "../../hooks/useAuth";
import { partyShellIdToModePathSegment } from "../../modes/shellToModeApi";
import { LongPressProgressRing } from "../../party/LongPressProgressRing";
import { partyShellSupportsNativeTvCast } from "../../party/modePartyCards";
import { buildPartyCastRevealUrl } from "../../party/partyCastPayload";
import PartyCastQrPanel from "../../party/PartyCastQrPanel";
import PartyCastSourceToggle from "../../party/PartyCastSourceToggle";
import PartyShareResultButton from "../../party/share/PartyShareResultButton";
import { usePartyShareModeTitle } from "../../party/share/usePartyShareModeTitle";
import { PartyTimer, PartyTopBar } from "../../party/PartyChrome";
import { PartyMascot } from "../../party/PartyMascot";
import { handlePartyTeamNameEnterFocusNext, isPartyNameSubmitKey } from "../../party/partyNameInputEnter";
import { PartySavedRosterSetupSection } from "../../party/PartySavedRosterSetupSection";
import { PartyPlayResumePrompt } from "../../party/PartyPlayResumePrompt";
import { PartySuggestCategoryDrawer } from "../../party/PartySuggestCategoryDrawer";
import { usePartySavedRoster } from "../../party/usePartySavedRoster";
import { TRIAL_ACTING_DECK } from "../../party/partyTrialMockData";
import { PartyTrialSetupBanner } from "../../party/PartyTrialSetupBanner";
import { usePartyPlayTrialMode } from "../../party/usePartyPlayTrialMode";
import type { PartyShellPalette } from "../../theme/theme";
import type { ModeCategoryRow, ModeWordContentRow } from "../../types/api";
import { apiClient } from "../../utils/api";
import { trackPartyGameCompleted, trackPartyGameStarted } from "../../utils/mixpanel";
import { modeCategoryDisplayName, modeCategoryNamesFromIds } from "../../utils/modeCategoryLabels";
import { clearPartyGameCache, readPartyGameCache, writePartyGameCache } from "../../utils/partyGameLocalCache";

const SEG = partyShellIdToModePathSegment("act");
const CACHE_SHELL = "act";
const LONG_PRESS_MS = 750;

type Phase = "setup" | "play" | "end";
type TeamSide = "a" | "b";

type ActingCacheData = {
  phase: Phase;
  picked: number[];
  teamA: string;
  teamB: string;
  deck: { team: TeamSide; word: string }[];
  idx: number;
  seconds: number;
  scoreA: number;
  scoreB: number;
  wordRevealed: boolean;
};
type ScorePanelPlayVariant = "activeA" | "inactiveA" | "activeB" | "inactiveB";
type FinalPanelVariant = "winnerA" | "loserOrTieA" | "winnerB" | "loserOrTieB";

// --- long press reveal (word hidden until hold completes) -------------------

function ActingPressToReveal({ label, onReveal }: { label: string; onReveal: () => void }) {
  const [progress, setProgress] = useState(0);
  const [pressed, setPressed] = useState(false);
  const rafRef = useRef<number | null>(null);
  const startRef = useRef<number | null>(null);
  const revealedRef = useRef(false);

  const stopRaf = () => {
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  };

  const reset = () => {
    stopRaf();
    startRef.current = null;
    revealedRef.current = false;
    setProgress(0);
    setPressed(false);
  };

  const tick = () => {
    const start = startRef.current;
    if (start == null) return;
    const elapsed = performance.now() - start;
    const p = Math.min(1, elapsed / LONG_PRESS_MS);
    setProgress(p);
    if (p >= 1) {
      stopRaf();
      setPressed(false);
      if (!revealedRef.current) {
        revealedRef.current = true;
        onReveal();
      }
      return;
    }
    rafRef.current = requestAnimationFrame(tick);
  };

  const onPointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (e.button !== 0 && e.pointerType === "mouse") return;
    e.preventDefault();
    const el = e.currentTarget;
    try {
      el.setPointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
    revealedRef.current = false;
    startRef.current = performance.now();
    setPressed(true);
    stopRaf();
    rafRef.current = requestAnimationFrame(tick);
  };

  const onPointerEnd = (e: React.PointerEvent<HTMLButtonElement>) => {
    const el = e.currentTarget;
    try {
      if (el.hasPointerCapture(e.pointerId)) {
        el.releasePointerCapture(e.pointerId);
      }
    } catch {
      /* ignore */
    }
    setPressed(false);
    if (revealedRef.current) return;
    const start = startRef.current;
    if (start == null) return;
    const elapsed = performance.now() - start;
    if (elapsed < LONG_PRESS_MS) {
      reset();
    }
  };

  return (
    <Button
      type="button"
      variant="text"
      disableRipple
      onPointerDown={onPointerDown}
      onPointerUp={onPointerEnd}
      onPointerCancel={onPointerEnd}
      onPointerLeave={(e) => {
        if (revealedRef.current) return;
        try {
          if (e.currentTarget.hasPointerCapture(e.pointerId)) return;
        } catch {
          /* ignore */
        }
        reset();
      }}
      sx={{
        p: 0,
        minWidth: 200,
        width: 200,
        height: 200,
        mx: "auto",
        display: "block",
        borderRadius: 999,
        bgcolor: "rgba(255,255,255,0.08)",
        position: "relative",
        touchAction: "none",
        userSelect: "none",
        WebkitUserSelect: "none",
        transform: pressed ? "scale(0.94)" : "scale(1)",
        boxShadow: pressed ? "inset 0 8px 24px rgba(0,0,0,0.35)" : "inset 0 0 0 transparent",
        transition: "transform 0.12s ease, box-shadow 0.12s ease",
      }}
    >
      {pressed && progress < 1 ? (
        <LongPressProgressRing
          progress={progress}
          trackStroke="color-mix(in oklab, var(--hot) 45%, transparent)"
          progressStroke="var(--hot)"
        />
      ) : null}
      <Typography
        component="span"
        sx={{
          position: "relative",
          zIndex: 1,
          fontSize: 13,
          fontWeight: 800,
          color: "var(--ink)",
          px: 2,
          textAlign: "center",
          lineHeight: 1.35,
        }}
      >
        {label}
      </Typography>
    </Button>
  );
}

// --- styled layout (aligned with PartyPlayGuessIt) --------------------------

const SetupRoot = styled(Box)({
  padding: "4px 20px 100px",
});

const PlayRoot = styled(Box)({
  height: "100dvh",
  display: "flex",
  flexDirection: "column",
  padding: 16,
});

const EndRoot = styled(Box)({
  padding: "16px 20px 100px",
  textAlign: "center",
  minHeight: "100dvh",
  boxSizing: "border-box",
});

const MascotWrap = styled(Box)({
  marginTop: 8,
  marginBottom: 16,
});

const HintText = styled(Typography)({
  display: "block",
  color: "var(--ink-dim)",
  fontSize: 14,
  marginBottom: 16,
});

const TeamField = styled(TextField)({
  flex: 1,
  "& .MuiOutlinedInput-root": {
    borderRadius: 12,
    backgroundColor: "rgba(255,255,255,0.05)",
    color: "var(--ink)",
    "& fieldset": {
      borderColor: "var(--ink-faint)",
      borderWidth: 1.5,
    },
    "&:hover fieldset": {
      borderColor: "var(--ink-faint)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "var(--hot)",
    },
  },
});

const CategoryPickButton = styled("button", {
  shouldForwardProp: (p) => p !== "picked",
})<{ picked?: boolean }>(({ picked }) => ({
  width: "100%",
  marginBottom: 8,
  padding: "14px 16px",
  borderRadius: 16,
  font: "inherit",
  textAlign: "start",
  cursor: "pointer",
  fontWeight: 700,
  color: "var(--ink)",
  border: picked ? "2px solid var(--hot)" : "1.5px solid var(--ink-faint)",
  background: picked ? "rgba(255,61,120,0.12)" : "rgba(255,255,255,0.04)",
  "&:hover": {
    background: picked ? "rgba(255,61,120,0.16)" : "rgba(255,255,255,0.07)",
    borderColor: picked ? "var(--hot)" : "var(--ink-faint)",
  },
}));

const StartButton = styled(Button)({
  width: "100%",
  marginTop: 16,
});

const CreditErrorText = styled(Typography)({
  display: "block",
  color: "var(--hot)",
  marginTop: 8,
});

const TurnBanner = styled(Box, {
  shouldForwardProp: (p) => p !== "actingTeam",
})<{ actingTeam: TeamSide }>(({ actingTeam }) =>
  actingTeam === "a"
    ? {
      marginBottom: 12,
      padding: "14px 16px 16px",
      borderRadius: 20,
      textAlign: "center",
      background:
        "linear-gradient(165deg, color-mix(in oklab, var(--mint) 22%, transparent), rgba(255,255,255,0.04))",
      border: "1.5px solid color-mix(in oklab, var(--mint) 55%, transparent)",
      boxShadow: "0 0 0 2px rgba(120, 255, 214, 0.45), 0 16px 40px -12px rgba(120, 255, 214, 0.35)",
    }
    : {
      marginBottom: 12,
      padding: "14px 16px 16px",
      borderRadius: 20,
      textAlign: "center",
      background:
        "linear-gradient(165deg, color-mix(in oklab, var(--gold) 22%, transparent), rgba(255,255,255,0.04))",
      border: "1.5px solid color-mix(in oklab, var(--gold) 55%, transparent)",
      boxShadow: "0 0 0 2px rgba(255, 200, 120, 0.5), 0 16px 40px -12px rgba(255, 200, 100, 0.3)",
    }
);

const TurnKicker = styled(Box)({
  fontSize: 11,
  letterSpacing: "0.14em",
  color: "var(--ink-dim)",
  marginBottom: 6,
});

const TurnName = styled(Box, {
  shouldForwardProp: (p) => p !== "actingTeam",
})<{ actingTeam: TeamSide }>(({ actingTeam }) => ({
  fontSize: "clamp(26px, 7vw, 40px)",
  lineHeight: 1.05,
  color: actingTeam === "a" ? "var(--mint)" : "var(--gold)",
}));

const CardProgressLine = styled(Box)({
  fontSize: 12,
  color: "var(--ink-dim)",
  textAlign: "center",
  marginBottom: 10,
});

const ScoresPlayRow = styled(Stack)({
  flexDirection: "row",
  justifyContent: "space-between",
  alignItems: "stretch",
  gap: 10,
  marginBottom: 10,
});

const ScoreTeamPanelPlay = styled(Box, {
  shouldForwardProp: (p) => p !== "panelVariant",
})<{ panelVariant: ScorePanelPlayVariant }>(({ panelVariant }) => {
  const activeA = panelVariant === "activeA";
  const activeB = panelVariant === "activeB";
  return {
    flex: 1,
    minWidth: 0,
    padding: "10px 12px",
    borderRadius: 16,
    textAlign: activeB ? "end" : "start",
    border: activeA
      ? "2px solid color-mix(in oklab, var(--mint) 70%, transparent)"
      : activeB
        ? "2px solid color-mix(in oklab, var(--gold) 70%, transparent)"
        : "1.5px solid var(--ink-faint)",
    background: activeA
      ? "color-mix(in oklab, var(--mint) 12%, transparent)"
      : activeB
        ? "color-mix(in oklab, var(--gold) 14%, transparent)"
        : "rgba(255,255,255,0.03)",
    opacity: activeA || activeB ? 1 : 0.72,
    transform: activeA || activeB ? "scale(1.02)" : "scale(1)",
    transition: "opacity 0.2s ease, transform 0.2s ease",
  };
});

const TimerWrap = styled(Stack)({
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
});

const PanelMeta = styled(Box)({
  fontSize: 9,
  color: "var(--ink-dim)",
  letterSpacing: "0.06em",
});

const PanelTeamName = styled(Box)({
  fontSize: 11,
  color: "var(--ink)",
  fontWeight: 800,
  marginTop: 2,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
});

const ScoreBigPlay = styled(Box, {
  shouldForwardProp: (p) => p !== "team",
})<{ team: TeamSide }>(({ team }) => ({
  fontSize: 34,
  lineHeight: 1,
  marginTop: 4,
  color: team === "a" ? "var(--mint)" : "var(--gold)",
}));

const HiddenWordArea = styled(Box)({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center",
  padding: 16,
  minHeight: 0,
  gap: 12,
});

const PassInstruction = styled(Box)({
  color: "var(--ink-dim)",
  fontSize: 15,
  lineHeight: 1.55,
  maxWidth: 360,
  marginBottom: 16,
});

const WordCard = styled(Box)({
  "--card-bg": "var(--paper)",
  color: "var(--paper-ink)",
  flex: 1,
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  textAlign: "center",
  padding: 24,
  minHeight: 0,
});

const RevealedHint = styled(Box)({
  marginTop: 12,
  fontSize: 13,
  color: "var(--ink-dim)",
});

const PlayActionsGrid = styled(Box)({
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 8,
  marginTop: 12,
});

const SkipButton = styled(Button)({
  width: "100%",
  marginTop: 8,
});

const SkipButtonHidden = styled(Button)({
  width: "100%",
  maxWidth: 320,
  marginTop: 4,
});

const WinnerKicker = styled(Box)({
  fontSize: 12,
  letterSpacing: "0.2em",
  color: "var(--hot)",
  fontWeight: 800,
  textShadow: "0 0 24px rgba(255, 61, 120, 0.35)",
});

const WinnerName = styled(Box, {
  shouldForwardProp: (p) => p !== "winnerIsA",
})<{ winnerIsA: boolean }>(({ winnerIsA }) => ({
  fontSize: "clamp(36px, 11vw, 52px)",
  lineHeight: 1.05,
  marginTop: 10,
  color: winnerIsA ? "var(--mint)" : "var(--gold)",
  textShadow: winnerIsA
    ? "0 0 60px -10px rgba(120, 255, 214, 0.45)"
    : "0 0 60px -10px rgba(255, 200, 100, 0.4)",
}));

const WinnerSub = styled(Box)({
  fontSize: 14,
  color: "var(--ink-dim)",
  marginTop: 10,
  fontWeight: 700,
});

const TieOverline = styled(Box)({
  fontSize: 12,
  letterSpacing: "0.12em",
  color: "var(--ink-dim)",
});

const TieTitle = styled(Box)({
  fontSize: "clamp(32px, 9vw, 44px)",
  marginTop: 8,
  color: "var(--ink)",
});

const FinalScoreGrid = styled(Box)({
  display: "grid",
  gridTemplateColumns: "1fr auto 1fr",
  alignItems: "stretch",
  gap: 12,
  marginTop: 28,
  maxWidth: 420,
  marginLeft: "auto",
  marginRight: "auto",
});

const FinalScoreCell = styled(Box, {
  shouldForwardProp: (p) => p !== "panelVariant",
})<{ panelVariant: FinalPanelVariant }>(({ panelVariant }) => {
  const isWinnerA = panelVariant === "winnerA";
  const isWinnerB = panelVariant === "winnerB";
  return {
    padding: "16px 14px",
    borderRadius: 18,
    border: isWinnerA
      ? "2px solid color-mix(in oklab, var(--mint) 65%, transparent)"
      : isWinnerB
        ? "2px solid color-mix(in oklab, var(--gold) 65%, transparent)"
        : "1.5px solid var(--ink-faint)",
    background: isWinnerA
      ? "color-mix(in oklab, var(--mint) 10%, transparent)"
      : isWinnerB
        ? "color-mix(in oklab, var(--gold) 12%, transparent)"
        : "rgba(255,255,255,0.04)",
    boxShadow: isWinnerA
      ? "0 12px 32px -14px rgba(120, 255, 214, 0.25)"
      : isWinnerB
        ? "0 12px 32px -14px rgba(255, 200, 100, 0.22)"
        : "none",
  };
});

const FinalTeamTitle = styled(Box)({
  fontSize: 10,
  color: "var(--ink-dim)",
});

const FinalTeamName = styled(Box)({
  fontSize: 13,
  fontWeight: 800,
  color: "var(--ink)",
  marginTop: 4,
  lineHeight: 1.2,
  minHeight: "2.4em",
});

const FinalScoreNumber = styled(Box, {
  shouldForwardProp: (p) => p !== "team",
})<{ team: TeamSide }>(({ team }) => ({
  fontSize: 44,
  marginTop: 8,
  lineHeight: 1,
  color: team === "a" ? "var(--mint)" : "var(--gold)",
}));

const FinalPointsLabel = styled(Box)({
  fontSize: 10,
  color: "var(--ink-dim)",
  marginTop: 4,
});

const ScoreColon = styled(Box)(({ theme }) => ({
  fontFamily: theme.typography.fontFamily,
  fontSize: 28,
  color: "var(--ink-dim)",
  opacity: 0.85,
  fontWeight: 900,
}));

const ColonWrap = styled(Stack)({
  alignItems: "center",
  justifyContent: "center",
});

const HomeButton = styled(Button)({
  width: "100%",
  maxWidth: 420,
  marginTop: 28,
  marginLeft: "auto",
  marginRight: "auto",
  display: "block",
});

// --- screen -----------------------------------------------------------------

export default function PartyPlayActing() {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const partyPal = (theme.palette as unknown as { party: PartyShellPalette }).party;
  const shareModeTitle = usePartyShareModeTitle("act", i18n.language?.startsWith("ar") ? "تمثيل" : "Acting");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isTrialMode = usePartyPlayTrialMode();
  const savedRoster = usePartySavedRoster();
  const [phase, setPhase] = useState<Phase>("setup");
  const [picked, setPicked] = useState<number[]>([]);
  const [teamA, setTeamA] = useState("");
  const [teamB, setTeamB] = useState("");
  const [deck, setDeck] = useState<{ team: TeamSide; word: string }[]>([]);
  const [idx, setIdx] = useState(0);
  const [seconds, setSeconds] = useState(60);
  const [scoreA, setScoreA] = useState(0);
  const [scoreB, setScoreB] = useState(0);
  const [wordRevealed, setWordRevealed] = useState(false);
  const [gameStartedAtMs, setGameStartedAtMs] = useState<number | null>(null);
  const completionTrackedRef = useRef(false);
  const [castByQr, setCastByQr] = useState(false);
  const [suggestCategoryOpen, setSuggestCategoryOpen] = useState(false);
  const nativeTvCast = partyShellSupportsNativeTvCast(CACHE_SHELL);
  const teamBFieldRef = useRef<HTMLInputElement | null>(null);
  const teamARef = useRef(teamA);
  const teamBRef = useRef(teamB);
  useEffect(() => {
    teamARef.current = teamA;
    teamBRef.current = teamB;
  }, [teamA, teamB]);

  const [cacheEnvelope] = useState(() => readPartyGameCache<ActingCacheData>(CACHE_SHELL));
  const [resumeDismissed, setResumeDismissed] = useState(false);
  const cachedActingPlay =
    cacheEnvelope?.data?.phase === "play" &&
    Array.isArray(cacheEnvelope.data.deck) &&
    cacheEnvelope.data.deck.length > 0 &&
    typeof cacheEnvelope.data.idx === "number" &&
    cacheEnvelope.data.idx < cacheEnvelope.data.deck.length;
  const showResumePrompt = Boolean(cachedActingPlay && !resumeDismissed);

  const catQ = useQuery({
    queryKey: ["modes", SEG, "categories"],
    queryFn: () => apiClient.getModeCategories(SEG),
    enabled: phase === "setup" && !isTrialMode,
  });

  const startMut = useMutation({
    mutationFn: (ids: [number, number]) => apiClient.postActingStart(ids),
    onSuccess: (data: { team_a_contents?: ModeWordContentRow[]; team_b_contents?: ModeWordContentRow[] }, ids) => {
      savedRoster.recordTeams(teamARef.current, teamBRef.current);
      const a = data?.team_a_contents ?? [];
      const b = data?.team_b_contents ?? [];
      const merged: { team: TeamSide; word: string }[] = [];
      const n = Math.max(a.length, b.length);
      for (let i = 0; i < n; i++) {
        if (a[i]?.word) merged.push({ team: "a", word: a[i].word });
        if (b[i]?.word) merged.push({ team: "b", word: b[i].word });
      }
      setDeck(merged);
      setIdx(0);
      setSeconds(60);
      setWordRevealed(false);
      setPhase("play");
      setGameStartedAtMs(Date.now());
      completionTrackedRef.current = false;
      invalidateUserSessionCredits(queryClient);
      trackPartyGameStarted({
        shell_id: "act",
        category_names: modeCategoryNamesFromIds([ids[0], ids[1]], catQ.data),
        number_of_players: 2,
        trial: false,
        mode_name_en: "Charades",
        team_a_name: teamA.trim() || undefined,
        team_b_name: teamB.trim() || undefined,
        deck_size: merged.length,
      });
    },
  });

  const beginActingTrial = useCallback(() => {
    savedRoster.recordTeams(teamARef.current, teamBRef.current);
    const merged = TRIAL_ACTING_DECK.map((c) => ({ ...c }));
    setDeck(merged);
    setIdx(0);
    setSeconds(60);
    setWordRevealed(false);
    setPhase("play");
    setGameStartedAtMs(Date.now());
    completionTrackedRef.current = false;
    trackPartyGameStarted({
      shell_id: "act",
      category_names: ["trial_sample"],
      number_of_players: 2,
      trial: true,
      mode_name_en: "Charades",
      team_a_name: teamA.trim() || undefined,
      team_b_name: teamB.trim() || undefined,
      deck_size: merged.length,
    });
  }, [savedRoster, teamA, teamB]);

  useEffect(() => {
    setWordRevealed(false);
    setSeconds(60);
  }, [idx, phase]);

  useEffect(() => {
    if (phase !== "play") return;
    if (!wordRevealed) return;
    if (seconds <= 0) return;
    const timer = window.setTimeout(() => setSeconds((s) => s - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [seconds, phase, wordRevealed]);

  useEffect(() => {
    if (phase === "end") {
      clearPartyGameCache(CACHE_SHELL);
      return;
    }
    const id = window.setTimeout(() => {
      const payload: ActingCacheData = {
        phase,
        picked,
        teamA,
        teamB,
        deck,
        idx,
        seconds,
        scoreA,
        scoreB,
        wordRevealed,
      };
      writePartyGameCache(CACHE_SHELL, payload);
    }, 400);
    return () => window.clearTimeout(id);
  }, [phase, picked, teamA, teamB, deck, idx, seconds, scoreA, scoreB, wordRevealed]);

  useEffect(() => {
    if (phase !== "end" || gameStartedAtMs == null || completionTrackedRef.current) return;
    completionTrackedRef.current = true;
    trackPartyGameCompleted({
      shell_id: "act",
      trial: isTrialMode,
      mode_name_en: "Charades",
      duration_seconds: Math.max(0, Math.round((Date.now() - gameStartedAtMs) / 1000)),
    });
  }, [gameStartedAtMs, isTrialMode, phase]);

  const applyActingCache = useCallback((data: ActingCacheData) => {
    const ph = data.phase === "play" || data.phase === "setup" ? data.phase : "setup";
    setPhase(ph);
    setPicked(Array.isArray(data.picked) ? data.picked : []);
    setTeamA(typeof data.teamA === "string" ? data.teamA : "");
    setTeamB(typeof data.teamB === "string" ? data.teamB : "");
    setDeck(Array.isArray(data.deck) ? data.deck : []);
    setIdx(typeof data.idx === "number" ? data.idx : 0);
    setSeconds(typeof data.seconds === "number" ? Math.max(0, Math.min(60, data.seconds)) : 60);
    setScoreA(typeof data.scoreA === "number" ? data.scoreA : 0);
    setScoreB(typeof data.scoreB === "number" ? data.scoreB : 0);
    setWordRevealed(Boolean(data.wordRevealed));
  }, []);

  const card = deck[idx];
  const actingTeam = card?.team ?? "a";

  const toggleCat = (id: number) => {
    setPicked((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= 2) return [prev[0], id];
      return [...prev, id];
    });
  };

  const catLabel = (c: ModeCategoryRow) => modeCategoryDisplayName(c, i18n.language);

  const canStart = picked.length === 2 && teamA.trim() && teamB.trim();
  const canTrialActing = Boolean(teamA.trim() && teamB.trim());
  const teamALabel = teamA.trim() || t("party.playActing.defaultTeamA");
  const teamBLabel = teamB.trim() || t("party.playActing.defaultTeamB");
  const actingLabel = actingTeam === "a" ? teamALabel : teamBLabel;

  const advanceDeck = useCallback(() => {
    setIdx((i) => {
      const n = i + 1;
      if (n >= deck.length) {
        setPhase("end");
        return i;
      }
      return n;
    });
  }, [deck.length]);

  const award = (team: TeamSide) => {
    if (!wordRevealed) return;
    if (team === "a") setScoreA((s) => s + 1);
    else setScoreB((s) => s + 1);
    advanceDeck();
  };

  if (phase === "setup") {
    return (
      <SetupRoot>
        <PartyTopBar title={t("party.playActing.setupTitleBar")} onBack={() => navigate("/party/detail/act")} />
        <Stack alignItems="center" py={1.5}>
          <PartyMascot shellId="act" size={100} />
        </Stack>
        <Typography className="party-display" sx={{ fontSize: 26, mb: 1 }}>
          {t("party.playActing.heading")}
        </Typography>
        {isTrialMode && <PartyTrialSetupBanner />}
        <HintText>{t("party.playActing.hint")}</HintText>
        <PartyCastSourceToggle
          nativeTvSupported={nativeTvCast}
          castByQr={castByQr}
          onChange={setCastByQr}
          partyPal={partyPal}
        />
        <Stack direction="row" gap={1} sx={{ mb: 1.5 }}>
          <TeamField
            size="small"
            value={teamA}
            onChange={(e) => setTeamA(e.target.value)}
            onKeyDown={(e) => handlePartyTeamNameEnterFocusNext(e, teamBFieldRef.current)}
            placeholder={t("party.playActing.teamAPlaceholder")}
            hiddenLabel
            InputProps={{ "aria-label": t("party.playActing.teamAPlaceholder") }}
          />
          <TeamField
            size="small"
            value={teamB}
            onChange={(e) => setTeamB(e.target.value)}
            onKeyDown={(e) => {
              if (!isPartyNameSubmitKey(e)) return;
              e.preventDefault();
            }}
            inputRef={teamBFieldRef}
            placeholder={t("party.playActing.teamBPlaceholder")}
            hiddenLabel
            InputProps={{ "aria-label": t("party.playActing.teamBPlaceholder") }}
          />
        </Stack>
        <PartySavedRosterSetupSection
          api={savedRoster}
          variant="teams"
          onApplySavedTeams={(a, b) => {
            setTeamA(a);
            setTeamB(b);
          }}
        />
        {!isTrialMode && catQ.isLoading && (
          <Typography sx={{ color: "var(--ink-dim)" }}>{t("party.playActing.loadingCategories")}</Typography>
        )}
        {!isTrialMode &&
          (catQ.data ?? []).map((c) => (
            <CategoryPickButton key={c.id} type="button" picked={picked.includes(c.id)} onClick={() => toggleCat(c.id)}>
              {catLabel(c)}
            </CategoryPickButton>
          ))}
        {!isTrialMode && (
          <>
            <Button
              type="button"
              variant="text"
              onClick={() => setSuggestCategoryOpen(true)}
              sx={{ mt: 1, color: partyPal.hot, fontWeight: 800, textTransform: "none", display: "block" }}
            >
              {t("party.suggestCategory.cta")}
            </Button>
            <PartySuggestCategoryDrawer
              open={suggestCategoryOpen}
              onClose={() => setSuggestCategoryOpen(false)}
              gameKey="acting"
              partyPal={partyPal}
            />
          </>
        )}
        {!isTrialMode && (startMut.error as { response?: { status?: number } })?.response?.status === 402 && (
          <CreditErrorText>{t("party.playActing.creditInsufficient")}</CreditErrorText>
        )}
        {showResumePrompt && cacheEnvelope ? (
          <PartyPlayResumePrompt
            savedAtIso={cacheEnvelope.savedAt}
            onContinue={() => {
              applyActingCache(cacheEnvelope.data);
              setResumeDismissed(true);
            }}
            onNewGame={() => {
              clearPartyGameCache(CACHE_SHELL);
              setResumeDismissed(true);
            }}
          />
        ) : (
          <StartButton
            type="button"
            disabled={isTrialMode ? !canTrialActing : !canStart || startMut.isPending}
            className="party-btn party-btn-hot"
            sx={{ opacity: (isTrialMode ? canTrialActing : canStart) ? 1 : 0.5 }}
            onClick={() =>
              isTrialMode ? beginActingTrial() : startMut.mutate([picked[0], picked[1]] as [number, number])
            }
          >
            {isTrialMode ? t("party.trial.continueSampleCta") : t("party.playActing.startGame")}
          </StartButton>
        )}
      </SetupRoot>
    );
  }

  if (phase === "play" && card) {
    const panelA: ScorePanelPlayVariant = actingTeam === "a" ? "activeA" : "inactiveA";
    const panelB: ScorePanelPlayVariant = actingTeam === "b" ? "activeB" : "inactiveB";

    return (
      <PlayRoot>
        <PartyTopBar
          title={t("party.playActing.playTitleBar")}
          onBack={() => {
            clearPartyGameCache(CACHE_SHELL);
            setPhase("setup");
          }}
        />
        <TurnBanner actingTeam={actingTeam} className="party-anim-fade-scale-in">
          <TurnKicker className="party-mono">{t("party.playActing.turnStripLabel")}</TurnKicker>
          <TurnName className="party-display" actingTeam={actingTeam}>
            {actingLabel}
          </TurnName>
        </TurnBanner>

        <CardProgressLine className="party-mono">
          {t("party.playActing.cardProgress", { current: idx + 1, total: deck.length })}
        </CardProgressLine>

        <ScoresPlayRow>
          <ScoreTeamPanelPlay panelVariant={panelA}>
            <PanelMeta className="party-mono">{t("party.playActing.teamAShort")}</PanelMeta>
            <PanelTeamName className="party-mono">{teamALabel}</PanelTeamName>
            <ScoreBigPlay className="party-display" team="a">
              {scoreA}
            </ScoreBigPlay>
          </ScoreTeamPanelPlay>
          <TimerWrap>
            <PartyTimer seconds={wordRevealed ? seconds : 60} total={60} />
          </TimerWrap>
          <ScoreTeamPanelPlay panelVariant={panelB}>
            <PanelMeta className="party-mono">{t("party.playActing.teamBShort")}</PanelMeta>
            <PanelTeamName className="party-mono">{teamBLabel}</PanelTeamName>
            <ScoreBigPlay className="party-display" team="b">
              {scoreB}
            </ScoreBigPlay>
          </ScoreTeamPanelPlay>
        </ScoresPlayRow>

        {wordRevealed ? (
          <>
            {!castByQr ? (
              <WordCard className="party-kan-card party-anim-fade-scale-in">
                <Typography className="party-display" sx={{ fontSize: "clamp(48px, 12vw, 80px)" }} component="div">
                  {card.word}
                </Typography>
                <RevealedHint className="party-mono">{t("party.playActing.revealedActHint")}</RevealedHint>
              </WordCard>
            ) : null}
            <PlayActionsGrid>
              <Button type="button" className="party-btn party-btn-hot" onClick={() => award("a")}>
                {t("party.playActing.scoreTeam", { team: teamALabel })}
              </Button>
              <Button type="button" className="party-btn party-btn-hot" onClick={() => award("b")}>
                {t("party.playActing.scoreTeam", { team: teamBLabel })}
              </Button>
            </PlayActionsGrid>
            <SkipButton type="button" className="party-btn party-btn-ghost" onClick={advanceDeck}>
              {t("party.playActing.skipNoPoints")}
            </SkipButton>
          </>
        ) : castByQr ? (
          <HiddenWordArea>
            <PartyCastQrPanel
              partyPal={partyPal}
              url={buildPartyCastRevealUrl("act", {
                v: 1,
                shell: "act",
                rtl: Boolean(i18n.language?.startsWith("ar")),
                layout: "acting_word",
                h1: t("party.cast.wordCardLabel"),
                w: card.word,
              })}
            />
            <Button type="button" className="party-btn party-btn-hot" sx={{ mt: 2, maxWidth: 360, mx: "auto", display: "block" }} onClick={() => setWordRevealed(true)}>
              {t("party.cast.continueAfterQr")}
            </Button>
            <SkipButtonHidden type="button" className="party-btn party-btn-ghost" onClick={advanceDeck}>
              {t("party.playActing.skipNoPoints")}
            </SkipButtonHidden>
          </HiddenWordArea>
        ) : (
          <HiddenWordArea>
            <PassInstruction>{t("party.playActing.passPhoneInstruction", { team: actingLabel })}</PassInstruction>
            <ActingPressToReveal label={t("party.playActing.pressHoldReveal")} onReveal={() => setWordRevealed(true)} />
            <SkipButtonHidden type="button" className="party-btn party-btn-ghost" onClick={advanceDeck}>
              {t("party.playActing.skipNoPoints")}
            </SkipButtonHidden>
          </HiddenWordArea>
        )}
      </PlayRoot>
    );
  }

  if (phase === "end") {
    const isTie = scoreA === scoreB;
    const winnerIsA = scoreA > scoreB;
    const winnerLabel = isTie ? null : winnerIsA ? teamALabel : teamBLabel;
    const finalA: FinalPanelVariant = !isTie && winnerIsA ? "winnerA" : "loserOrTieA";
    const finalB: FinalPanelVariant = !isTie && !winnerIsA ? "winnerB" : "loserOrTieB";

    return (
      <EndRoot>
        <PartyTopBar title={t("party.playActing.endTitleBar")} onBack={() => navigate("/party/detail/act")} />
        <MascotWrap>
          <PartyMascot shellId="act" size={isTie ? 96 : 120} />
        </MascotWrap>

        {isTie ? (
          <>
            <TieOverline className="party-mono">{t("party.playActing.tieOverline")}</TieOverline>
            <TieTitle className="party-display">{t("party.playActing.tieTitle")}</TieTitle>
          </>
        ) : (
          <>
            <WinnerKicker className="party-mono">{t("party.playActing.winnerKicker")}</WinnerKicker>
            <WinnerName className="party-display party-anim-fade-scale-in" winnerIsA={winnerIsA}>
              {winnerLabel}
            </WinnerName>
            <WinnerSub className="party-mono">{t("party.playActing.winnerSub")}</WinnerSub>
          </>
        )}

        <FinalScoreGrid>
          <FinalScoreCell panelVariant={finalA}>
            <FinalTeamTitle className="party-mono">{t("party.playActing.teamAShort")}</FinalTeamTitle>
            <FinalTeamName>{teamALabel}</FinalTeamName>
            <FinalScoreNumber className="party-display" team="a">
              {scoreA}
            </FinalScoreNumber>
            <FinalPointsLabel className="party-mono">
              {scoreA === 1 ? t("party.playActing.point") : t("party.playActing.points")}
            </FinalPointsLabel>
          </FinalScoreCell>
          <ColonWrap>
            <ScoreColon className="party-display">:</ScoreColon>
          </ColonWrap>
          <FinalScoreCell panelVariant={finalB}>
            <FinalTeamTitle className="party-mono">{t("party.playActing.teamBShort")}</FinalTeamTitle>
            <FinalTeamName>{teamBLabel}</FinalTeamName>
            <FinalScoreNumber className="party-display" team="b">
              {scoreB}
            </FinalScoreNumber>
            <FinalPointsLabel className="party-mono">
              {scoreB === 1 ? t("party.playActing.point") : t("party.playActing.points")}
            </FinalPointsLabel>
          </FinalScoreCell>
        </FinalScoreGrid>

        <PartyShareResultButton
          className="party-btn party-btn-paper"
          sx={{ width: "100%", maxWidth: 420, mt: 2 }}
          model={{
            shellId: "act",
            modeTitle: shareModeTitle,
            resultTitle: i18n.language?.startsWith("ar") ? "النتيجة" : "Result",
            hero: isTie ? (i18n.language?.startsWith("ar") ? "تعادل!" : "Tie!") : String(winnerLabel ?? ""),
            sub: isTie ? undefined : i18n.language?.startsWith("ar") ? "فاز/ت بالجولة" : "Won the round",
            scores: [
              { label: teamALabel, value: scoreA, color: "var(--mint)", highlight: !isTie && winnerIsA },
              { label: teamBLabel, value: scoreB, color: "var(--gold)", highlight: !isTie && !winnerIsA },
            ],
          }}
        />

        <HomeButton type="button" className="party-btn party-btn-hot" onClick={() => navigate("/")}>
          {t("party.playActing.home")}
        </HomeButton>
      </EndRoot>
    );
  }

  return null;
}
